import { dayjs } from '../../support/helpers/dayjs.ts';
import { State } from '@autopylot-internal/state';
import { APIClient } from '@autopylot-internal/autopylot-api-client';
import { ENTITLEMENTS } from './RevenueCatModel.ts';
import { IOfferMeta } from './IOfferMeta.ts';
import { Logger } from '../../support/debug/Logger.ts';

const ONE_HOUR_MS = 1000 * 60 * 60;

interface IEntitlementApiResponse {
    entitlement: string;
    store: 'promotional' | 'play_store' | 'app_store' | 'rc_billing';
    isSandbox?: boolean;
    expires_date?: string;
    product_identifier?: string;
    links: {
        // will exist if we can revoke it (a promotional entitlement and permission)
        self?: { href: string };
    }
}
interface IEntitlementsApiResponse {
    entitlements: IEntitlementApiResponse[];

    offerMeta: IOfferMeta;

    isDown: boolean;

    links: {
        self: {
            href: string;

            // will contain POST if can grant e_plus
            actions: string[]
        }
    }
}

export class RevenueCatAPIModel extends State<Record<string, never>> {
    static inject = () => [
        APIClient,
        Logger
    ];
    constructor(
        private apiClient: APIClient,
        private logger: Logger
    ){
        super({});
    }
    // won't exist, even after init, if a viewer
    private entitlementsApiResponse?: IEntitlementsApiResponse;

    canBypassPayments = () => {
        return Boolean(this.entitlementsApiResponse?.links.self.actions.includes('POST_e_plus'));
    };

    hasUpgraded = () => Boolean(this.findEntitlement());

    init = async () => {
        this.logger.info('RevenueCatAPIModel.init-ing');
        await this.fetchEntitlements();
        this.logger.info('RevenueCatAPIModel.init-ed', this.getDiagnostics().diagnostics);
    };

    private refreshOnExpiredTimeoutId: NodeJS.Timeout | undefined = undefined;
    private refreshOnExpiresDate?: string;

    private checkForRefreshOnExpired = () => {

        if (this.refreshOnExpiredTimeoutId){
            clearTimeout(this.refreshOnExpiredTimeoutId);
            this.refreshOnExpiresDate = undefined;
        }

        const expires_date = this.entitlementsApiResponse?.entitlements.find(e => e.entitlement === ENTITLEMENTS.PLUS)?.expires_date;

        if (expires_date){
            const msTilExpires = dayjs(expires_date).valueOf() - Date.now();
            if (msTilExpires < ONE_HOUR_MS) {
                this.refreshOnExpiredTimeoutId = setTimeout(() => {
                    this.refreshOnExpiredTimeoutId = undefined;
                    this.refreshOnExpiresDate = undefined;
                    this.fetchEntitlements();
                }, msTilExpires + 1000);
                this.refreshOnExpiresDate = expires_date;
            }
        }
    };

    fetchEntitlements = async () => {
        const entry = await this.apiClient.entry();

        // nothing to check if there is no link (eg b/c a viewer)
        if (!entry.links.entitlements){
            return;
        }

        this.entitlementsApiResponse = await this.apiClient.get(entry.links.entitlements);
        this.checkForRefreshOnExpired();
        this.setState({});
    };

    isDown = () => {
        return Boolean(this.entitlementsApiResponse?.isDown);
    };

    upgrade = async () => {
        if (this.hasUpgraded()){
            throw new Error('cannot upgrade, user has already upgraded');
        }
        if (!this.canBypassPayments()){
            throw new Error('cannot bypass payments');
        }

        const entry = await this.apiClient.entry();

        await this.apiClient.post(entry.links.entitlements, { entitlement: ENTITLEMENTS.PLUS });

        await this.fetchEntitlements();
    };

    private findEntitlement = () => {
        return (this.entitlementsApiResponse?.entitlements || []).find(e => e.entitlement === ENTITLEMENTS.PLUS);
    };

    hasSelfLink = () => {
        return Boolean(this.findEntitlement()?.links.self);
    };

    downgradeWithSelfLink = async () => {
        const entitlement = this.findEntitlement();
        if (!entitlement || !entitlement.links.self){
            throw new Error('downgradeWithSelfLink was called when there is no entitlement or self link');
        }

        await this.apiClient.delete(entitlement.links.self, {});

        await this.fetchEntitlements();
    };

    getDiagnostics = () => {
        const diagnostics: { [label: string]: any } = {
            'hasUpgraded': this.hasUpgraded(),
            entitlements: (this.entitlementsApiResponse?.entitlements || []).map(e => e.entitlement),
            entitlementsApiResponse: this.entitlementsApiResponse,
            canBypassPayments: this.canBypassPayments(),
            offerMeta: this.getOfferMeta(),
            refreshOnExpiresDate: this.refreshOnExpiresDate ? `${this.refreshOnExpiresDate} (${dayjs(this.refreshOnExpiresDate).format('h:mm:ss a z')})`: undefined,
        };

        return {
            diagnostics,
            diagnosticsMethods: {
                refresh: () => this.fetchEntitlements()
            }
        };
    };

    getOfferMeta = () => {
        return this.entitlementsApiResponse?.offerMeta || {} as IOfferMeta;
    };
}
